import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../config/api';
import Pagination from '../../components/Pagination';
import useAuthStore from '../../store/authStore';
import useCurrency from '../../hooks/useCurrency';

const DEFAULT_CATEGORIES = ['Rent', 'Utilities', 'Salaries', 'Transport', 'Maintenance', 'Marketing', 'Office Supplies', 'Other'];
const FREQUENCIES = [{ value: 'daily', label: 'Daily' }, { value: 'weekly', label: 'Weekly' }, { value: 'monthly', label: 'Monthly' }, { value: 'quarterly', label: 'Every 3 months' }, { value: 'yearly', label: 'Yearly' }];
const STATUS_COLORS = { paid: 'bg-green-100 text-green-700', pending: 'bg-yellow-100 text-yellow-700' };

const EMPTY_FORM = {
  category: 'Rent', description: '', amount: '', currency: 'USD', payment_method_id: '',
  date: new Date().toISOString().split('T')[0], notes: '', recurring: false,
  recurring_frequency: 'monthly', next_due_date: '', status: 'paid'
};

export default function ExpenseList() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const { secCode, showSec } = useCurrency();

  const [showForm,  setShowForm]  = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [form,      setForm]      = useState(EMPTY_FORM);
  const [error,     setError]     = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus,   setFilterStatus]   = useState('');
  const [dateFrom,       setDateFrom]       = useState('');
  const [dateTo,         setDateTo]         = useState('');
  const [page,           setPage]           = useState(1);
  const LIMIT = 50;
  const resetPage = () => setPage(1);

  const { data: expResult, isLoading } = useQuery({
    queryKey: ['expenses', filterCategory, filterStatus, dateFrom, dateTo, page],
    queryFn: () => api.get('/api/expenses', { params: {
      ...(filterCategory && { category: filterCategory }),
      ...(filterStatus   && { status: filterStatus }),
      ...(dateFrom       && { date_from: dateFrom }),
      ...(dateTo         && { date_to: dateTo }),
      page, limit: LIMIT,
    }}).then(r => r.data),
    keepPreviousData: true,
  });
  const expenses   = expResult?.data  || [];
  const totalExp   = expResult?.total || 0;
  const totalPages = expResult?.pages || 1;

  const { data: paymentMethods = [] } = useQuery({ queryKey: ['payment-methods'], queryFn: () => api.get('/api/payment-methods').then(r => r.data) });
  // the shop's own categories (Settings of categories: + / rename / switch off)
  const { data: catRows = [] } = useQuery({ queryKey: ['expense-categories'], queryFn: () => api.get('/api/expense-categories').then(r => r.data) });
  const CATEGORIES = catRows.length ? catRows.filter(c => c.active !== false || c.name === form.category).map(c => c.name) : DEFAULT_CATEGORIES;
  const ALL_CATEGORIES = catRows.length ? catRows.map(c => c.name) : DEFAULT_CATEGORIES;
  const [showCats, setShowCats] = useState(false);

  const saveMutation = useMutation({
    mutationFn: (data) => editing ? api.put(`/api/expenses/${editing._id}`, data) : api.post('/api/expenses', data),
    onSuccess: () => { queryClient.invalidateQueries(['expenses']); setShowForm(false); setEditing(null); setForm(EMPTY_FORM); setError(''); },
    onError: (err) => setError(err.response?.data?.error || 'Error saving expense'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/api/expenses/${id}`),
    onSuccess: () => queryClient.invalidateQueries(['expenses']),
    onError: (err) => alert(err.response?.data?.error || 'Error deleting expense'),
  });

  const handleEdit = (exp) => {
    setEditing(exp);
    setForm({ category: exp.category, description: exp.description || '', amount: exp.amount, currency: exp.currency || 'USD', payment_method_id: exp.payment_method_id || '', date: new Date(exp.date).toISOString().split('T')[0], notes: exp.notes || '', recurring: exp.recurring || false, recurring_frequency: exp.recurring_frequency || 'monthly', next_due_date: exp.next_due_date ? new Date(exp.next_due_date).toISOString().split('T')[0] : '', status: exp.status || 'paid' });
    setShowForm(true); setError('');
  };

  const handleSubmit = (e) => { e.preventDefault(); setError(''); saveMutation.mutate(form); };

  const totalPaid    = expenses.filter(e => e.status === 'paid').reduce((s, e) => s + e.amount, 0);
  const totalPending = expenses.filter(e => e.status === 'pending').reduce((s, e) => s + e.amount, 0);
  const totalAll     = expenses.reduce((s, e) => s + e.amount, 0);
  const fmtDate = (d) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const inputCls = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white";

  return (
    <div className="p-6 text-gray-800">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div><h1 className="text-2xl font-bold text-gray-900">Expenses</h1><p className="text-gray-500 text-sm mt-0.5">Track all business expenses</p></div>
        <div className="flex gap-2">
          <button onClick={() => setShowCats(true)} className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium px-4 py-2 rounded-lg">🏷 Categories</button>
          <button onClick={async () => { try { const r = await api.post('/api/expenses/run-recurring'); alert(r.data.created ? `${r.data.created} recurring expense(s) created (to pay).` : 'No recurring expense is due.'); queryClient.invalidateQueries(['expenses']); } catch (e) { alert(e.response?.data?.error || e.message); } }} className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium px-4 py-2 rounded-lg">🔁 Create due recurring</button>
          <button onClick={() => { setShowForm(true); setEditing(null); setForm({ ...EMPTY_FORM, category: CATEGORIES[0] || 'Rent' }); setError(''); }} className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg">+ Add Expense</button>
        </div>
      </div>
      {showCats && <CategoriesModal rows={catRows} onClose={() => setShowCats(false)} onChanged={() => { queryClient.invalidateQueries(['expense-categories']); queryClient.invalidateQueries(['expenses']); }} />}

      <div className="grid grid-cols-3 gap-4 mb-5">
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm"><p className="text-xs text-gray-500 mb-1">Total Expenses</p><p className="text-2xl font-bold text-gray-800">${totalAll.toFixed(2)}</p></div>
        <div className="bg-white rounded-xl border border-green-200 p-4 shadow-sm"><p className="text-xs text-green-600 mb-1">Paid</p><p className="text-2xl font-bold text-green-600">${totalPaid.toFixed(2)}</p></div>
        <div className="bg-white rounded-xl border border-yellow-200 p-4 shadow-sm"><p className="text-xs text-yellow-600 mb-1">Pending</p><p className="text-2xl font-bold text-yellow-600">${totalPending.toFixed(2)}</p></div>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
          <h3 className="text-base font-bold text-gray-800 mb-4">{editing ? 'Edit Expense' : 'New Expense'}</h3>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Category *</label>
              <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className={inputCls}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
              <input type="text" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="e.g. Office rent for June" className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Date *</label>
              <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className={inputCls} required />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Amount *</label>
              <input type="number" value={form.amount} min="0" step="0.01" onChange={e => setForm({ ...form, amount: e.target.value })} className={inputCls} required placeholder="0.00" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Currency</label>
              <select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} className={inputCls}>
                <option value="USD">USD</option>
                {showSec && <option value={secCode}>{secCode}</option>}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Payment Method</label>
              <select value={form.payment_method_id} onChange={e => setForm({ ...form, payment_method_id: e.target.value })} className={inputCls}>
                <option value="">— Select —</option>
                {paymentMethods.map(m => <option key={m._id} value={m._id}>{m.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className={inputCls}>
                <option value="paid">Paid</option><option value="pending">Pending</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
              <input type="text" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Optional notes" className={inputCls} />
            </div>
            <div className="md:col-span-3 border-t border-gray-100 pt-4">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer mb-3">
                <input type="checkbox" checked={form.recurring} onChange={e => setForm({ ...form, recurring: e.target.checked })} className="w-4 h-4 rounded border-gray-300" />
                Recurring expense
              </label>
              {form.recurring && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Frequency</label>
                    <select value={form.recurring_frequency} onChange={e => setForm({ ...form, recurring_frequency: e.target.value })} className={inputCls}>
                      {FREQUENCIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Next Due Date</label>
                    <input type="date" value={form.next_due_date} onChange={e => setForm({ ...form, next_due_date: e.target.value })} className={inputCls} />
                  </div>
                </div>
              )}
            </div>
            {error && <div className="md:col-span-3 text-red-600 text-sm">{error}</div>}
            <div className="md:col-span-3 flex gap-3">
              <button type="submit" disabled={saveMutation.isPending} className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-6 py-2 rounded-lg disabled:opacity-50">{saveMutation.isPending ? 'Saving...' : 'Save Expense'}</button>
              <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium px-6 py-2 rounded-lg">Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 flex flex-wrap gap-3">
        <select value={filterCategory} onChange={e => { setFilterCategory(e.target.value); resetPage(); }} className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none">
          <option value="">All Categories</option>{ALL_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); resetPage(); }} className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none">
          <option value="">All Status</option><option value="paid">Paid</option><option value="pending">Pending</option>
        </select>
        <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); resetPage(); }} className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none" />
        <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); resetPage(); }} className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none" />
        {(filterCategory || filterStatus || dateFrom || dateTo) && <button onClick={() => { setFilterCategory(''); setFilterStatus(''); setDateFrom(''); setDateTo(''); resetPage(); }} className="text-sm text-blue-500 hover:text-blue-700 px-2">Clear</button>}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {isLoading ? <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>
        : expenses.length === 0 ? <div className="p-12 text-center text-gray-400">No expenses found.</div>
        : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Date</th>
                <th className="text-left px-4 py-3 font-semibold">Category</th>
                <th className="text-left px-4 py-3 font-semibold">Description</th>
                <th className="text-left px-4 py-3 font-semibold">Method</th>
                <th className="text-left px-4 py-3 font-semibold">Recurring</th>
                <th className="text-right px-4 py-3 font-semibold">Amount</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {expenses.map(exp => (
                <tr key={exp._id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-4 py-2.5 text-gray-500 text-xs whitespace-nowrap">{fmtDate(exp.date)}</td>
                  <td className="px-4 py-2.5"><span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium">{exp.category}</span></td>
                  <td className="px-4 py-2.5 text-gray-700 max-w-[200px] truncate">{exp.description || <span className="text-gray-300">—</span>}</td>
                  <td className="px-4 py-2.5 text-gray-600">{exp.payment_method_name || '—'}</td>
                  <td className="px-4 py-2.5">
                    {exp.recurring ? (
                      <div>
                        <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium capitalize">{exp.recurring_frequency}</span>
                        {exp.next_due_date && <div className="text-xs text-gray-400 mt-0.5">Next: {fmtDate(exp.next_due_date)}</div>}
                      </div>
                    ) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-2.5 text-right font-bold text-gray-800">
                    ${exp.amount.toFixed(2)}<span className="text-gray-400 font-normal text-xs ml-1">{exp.currency}</span>
                  </td>
                  <td className="px-4 py-2.5"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[exp.status] || 'bg-gray-100 text-gray-600'}`}>{exp.status}</span></td>
                  <td className="px-4 py-2.5">
                    <div className="flex gap-2">
                      <button onClick={() => handleEdit(exp)} className="text-blue-600 hover:text-blue-800 font-medium text-xs">Edit</button>
                      {user?.role === 'admin' && <button onClick={() => { if (window.confirm('Delete this expense?')) deleteMutation.mutate(exp._id); }} className="text-red-500 hover:text-red-700 font-medium text-xs">Delete</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
        }
        <Pagination page={page} pages={totalPages} total={totalExp} limit={LIMIT} onChange={(p) => setPage(p)} />
      </div>
    </div>
  );
}


// ── manage the expense categories: add, rename, link to an account, switch off ──
function CategoriesModal({ rows, onClose, onChanged }) {
  const [name, setName] = useState('');
  const { data: chart } = useQuery({ queryKey: ['acc-chart'], queryFn: () => api.get('/api/accounting/accounts').then(r => r.data), retry: false });
  const expAccounts = (chart?.accounts || []).filter(a => a.type === 'expense' && a.active);
  const err = (e) => alert(e.response?.data?.error || e.message);
  const add = async () => { if (!name.trim()) return; try { await api.post('/api/expense-categories', { name }); setName(''); onChanged(); } catch (e) { err(e); } };
  const rename = async (c) => { const n = window.prompt('New name (its expenses follow)', c.name); if (!n || n === c.name) return; try { await api.put(`/api/expense-categories/${c._id}`, { name: n }); onChanged(); } catch (e) { err(e); } };
  const toggle = async (c) => { try { await api.put(`/api/expense-categories/${c._id}`, { active: c.active === false }); onChanged(); } catch (e) { err(e); } };
  const link = async (c, code) => { try { await api.put(`/api/expense-categories/${c._id}`, { account_code: code || null }); onChanged(); } catch (e) { err(e); } };
  const del = async (c) => { if (!window.confirm(`Delete "${c.name}"?`)) return; try { const r = await api.delete(`/api/expense-categories/${c._id}`); if (r.data.note) alert(r.data.note); onChanged(); } catch (e) { err(e); } };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3"><h3 className="font-bold text-gray-800">Expense categories</h3><button onClick={onClose} className="text-xl text-gray-400">×</button></div>
        <div className="flex gap-2 mb-3"><input className="border rounded-lg px-3 py-2 text-sm flex-1" placeholder="New category (e.g. Generator, Internet…)" value={name} onChange={e => setName(e.target.value)} onKeyDown={e => e.key === 'Enter' && add()} /><button onClick={add} className="bg-blue-600 text-white text-sm px-4 rounded-lg">Add</button></div>
        <table className="w-full text-sm"><thead className="text-xs text-gray-500 border-b"><tr><th className="text-left py-1">Category</th><th className="text-left">Accounting account</th><th /></tr></thead><tbody>
          {rows.map(c => (
            <tr key={c._id} className={`border-b ${c.active === false ? 'text-gray-400' : ''}`}>
              <td className="py-1.5">{c.name}{c.active === false ? ' (off)' : ''}</td>
              <td>{expAccounts.length ? (
                <select className="border rounded px-2 py-1 text-xs" value={c.account_code || ''} onChange={e => link(c, e.target.value)}>
                  <option value="">automatic ("Expenses — {c.name}")</option>
                  {expAccounts.map(a => <option key={a.code} value={a.code}>{a.label}</option>)}
                </select>) : <span className="text-xs text-gray-400">automatic</span>}</td>
              <td className="text-right whitespace-nowrap">
                <button className="text-xs text-blue-600 px-1" onClick={() => rename(c)}>rename</button>
                <button className="text-xs text-gray-600 px-1" onClick={() => toggle(c)}>{c.active === false ? 'switch on' : 'switch off'}</button>
                <button className="text-xs text-red-500 px-1" onClick={() => del(c)}>delete</button>
              </td>
            </tr>))}
        </tbody></table>
      </div>
    </div>
  );
}
